import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminPreAuthLayout from '../layouts/AdminPreAuthLayout';
import PreAuthCard from '../../shared/components/PreAuthCard';
import FormInput from '../../shared/components/FormInput';
import IconButton from '../../shared/components/IconButton';
import LoadingView from '../../shared/components/LoadingView';
import { API_URL } from '../../config/env';

export default function InvitationAcceptPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [form, setForm] = useState({
    given_name: '',
    family_name: '',
    email: '',
    password: '',
    password_confirmation: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Extract token
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('invitation_token');

  // Verify token
  useEffect(() => {
    async function verifyToken() {
      try {
        const res = await fetch(
          `${API_URL}/v1/admin/users/invitation/accept.json?invitation_token=${token}`
        );
        if (!res.ok) {
          setExpired(true);
        } else {
          const data = await res.json();
          setForm(f => ({ ...f, email: data.email || '' }));
        }
      } catch (err) {
        console.error('Error verifying token:', err);
        setExpired(true);
      } finally {
        setLoading(false);
      }
    }
    if (token) verifyToken();
    else setExpired(true);
  }, [token]);

  // Validation
  const validateForm = () => {
    const newErrors = {};
    if (!form.given_name.trim()) newErrors.given_name = 'First name is required';
    if (!form.family_name.trim()) newErrors.family_name = 'Last name is required';
    if (form.password.length < 8 || !/[A-Z]/.test(form.password) || !/[a-z]/.test(form.password) || !/[0-9]/.test(form.password)) {
      newErrors.password = 'Password must be at least 8 chars with upper, lower, number';
    }
    if (form.password_confirmation !== form.password) {
      newErrors.password_confirmation = 'Passwords do not match';
    }
    return newErrors;
  };

  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSubmitting(true);
    try {
      const res = await fetch(
        `${API_URL}/v1/admin/users/invitation`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user: { ...form, invitation_token: token } })
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Unable to complete setup');
      }
      navigate('/admin/login');
    } catch (err) {
      setErrors({ general: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  // UI
  if (loading) {
    return (
      <AdminPreAuthLayout>
        <LoadingView
          symbolClass="bi bi-envelope-open"
          title="Validating invitation"
          subtitle="Please wait while we verify your invite link."
          className="py-5"
        />
      </AdminPreAuthLayout>
    );
  }

  if (expired) {
    return (
      <AdminPreAuthLayout>
        <div className="alert alert-danger">Invitation expired or invalid.</div>
      </AdminPreAuthLayout>
    );
  }

  return (
    <AdminPreAuthLayout>
      <PreAuthCard>
        <div className="text-center mb-3">
          <h1 className="fs-4 fw-semibold mb-1">Complete your setup</h1>
          <p className="text-muted mb-0">
            Set up your password and provide your details to get started
          </p>
        </div>
        {errors.general && (
          <div className="alert alert-danger py-2 small">{errors.general}</div>
        )}
        <form onSubmit={handleSubmit} autoComplete="on">
          <div className="row">
            <div className="col">
              <FormInput
                id="given_name"
                name="given-name"
                label="First name"
                autoComplete="given-name"
                value={form.given_name}
                onChange={e => handleChange('given_name', e.target.value)}
                error={errors.given_name}
                required
              />
            </div>
            <div className="col">
              <FormInput
                id="family_name"
                name="family-name"
                label="Last name"
                autoComplete="family-name"
                value={form.family_name}
                onChange={e => handleChange('family_name', e.target.value)}
                error={errors.family_name}
                required
              />
            </div>
          </div>

          <FormInput
            id="email"
            type="email"
            name="email"
            label="Email address"
            autoComplete="email"
            value={form.email}
            onChange={() => {}}
            readOnly
          />

          <FormInput
            id="password"
            type="password"
            name="new-password"
            label="Password"
            autoComplete="new-password"
            value={form.password}
            onChange={e => handleChange('password', e.target.value)}
            error={errors.password}
            required
          />

          <FormInput
            id="password_confirmation"
            type="password"
            name="confirm-password"
            label="Confirm password"
            autoComplete="new-password"
            value={form.password_confirmation}
            onChange={e => handleChange('password_confirmation', e.target.value)}
            error={errors.password_confirmation}
            required
          />

          <IconButton
            type="submit"
            className="btn btn-primary w-100 py-3 fw-bold"
            icon="bi-check2"
            disabled={submitting}
          >
            Complete setup
          </IconButton>
        </form>
      </PreAuthCard>
    </AdminPreAuthLayout>
  );
}
